import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import CheckboxField from '../../components/Fields/CheckboxField';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import MultiSelectField from '../../components/Fields/MultiSelectField';
import TextboxField from '../../components/Fields/TextboxField';
import { FieldConfig, FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import FunctionalRoleModel from '../../models/FunctionalRoleModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import FunctionalRole_tab_Employees from './Tabs/FunctionalRole_tab_Employees';

const FunctionalRoleView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [formData, setFormData] = useState<FunctionalRoleModel>();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes', '-- Select item type');
    const projectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes', '-- Select project type');
    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes', '-- Select business-partner type');

    let functionalRoleId = 0;
    let idParams: IdParams = useParams();
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        functionalRoleId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (functionalRoleId !== undefined) {
            DataService.getById<FunctionalRoleModel>('api/v1/FunctionalRoles/', functionalRoleId)
                .then(response => {
                    setFormData(response);
                })
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [functionalRoleId]);

    function submit() {
        if (formData) {
            DataService
                .update<FunctionalRoleModel>(`api/v1/FunctionalRoles/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    let fields: FieldConfig[] = [
        { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'itemTypeIds', label: 'Item types'},
        { name: 'projectTypeIds', label: 'Project types'},
        { name: 'businessPartnerTypeIds', label: 'Business-partner types' },
        { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        { name: 'allowSaveWithoutCheckout', label: 'Allow saving an item without checking-out'},
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);
    const { isAuthorizedFor } = useAuth();


    if (functionalRoleId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.CreateFunctionalRole)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    return (
        <div className='main-content'>
            <Form
                title={formData.name}
                footer={(
                    <ButtonBar offset={2}>
                        <Authorize applicationFunction={ApplicationFunctions.UpdateFunctionalRole} >
                            <SaveButton  {...getSubmitButtonProps()} />
                        </Authorize>
                    </ButtonBar>
                )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} />
                            <MultiSelectField {...getFieldProps('projectTypeIds')} options={projectTypes} />
                            <MultiSelectField {...getFieldProps('businessPartnerTypeIds')} options={businessPartnerTypes} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                            <CheckboxField {...getFieldProps('allowSaveWithoutCheckout')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {functionalRoleId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey={'tab_employees'}
                                tabs={[
                                    {
                                        title: 'Employees',
                                        eventKey: 'tab_employees',
                                        component: <FunctionalRole_tab_Employees functionalRoleId={functionalRoleId} />
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default FunctionalRoleView;